import { EmbeddableCatalogBrowserWithoutRouter } from '@tableau/data-catalog-client-components';
import { WebClientApiContentTypeCountsResponse } from '@tableau/data-catalog-client-components/lib/src/components/CatalogBrowser/CatalogBrowserTypes';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Filter } from '~commonInfra/utils/Filter';
import { hideLaunchEditOnMobile } from '~commonInfra/utils/MobileHelper';
import { Projects } from '~src/projects/resources/Projects';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { SiteUrlsProps } from '../bootstrap/redux/selectors/SiteUrlsSelectors';
import { catalogFeatureFlagsAdapter } from '../catalog/CatalogFeatureFlagsAdapter';
import { DatabaseContentType, TableContentType } from '../spacesPlatform/models/ContentTypes';
import { CatalogApiRequests } from './CatalogApiRequests';
import { catalogApolloClient } from './CatalogApolloClient';
import { CatalogBrowserSubspaceExplorer } from './CatalogBrowserSubspaceExplorer';
import { registerErrorStore, unregisterErrorStore } from './CatalogErrorReporterService';
import { DatabaseDefinition } from './components/contentDefinitions/DatabaseDefinition';
import { TableDefinition } from './components/contentDefinitions/TableDefinition';
import { DataCatalogUIActionsService } from './services/DataCatalogUIActionsService';

type CatalogBrowserHostOwnProps = {};

export type ConnectedCatalogBrowserHostProps = SiteUrlsProps & {
  readonly areUsersVisible: boolean;
  readonly assetCountResult?: ISpaceInfoTypeItemCount<'catalog'>;
};

type CatalogBrowserHostProps =
  ConnectedCatalogBrowserHostProps &
  CatalogBrowserHostOwnProps &
  CatalogBrowserHostDispatchProps;
export interface CatalogBrowserHostDispatchProps {
  readonly fetchUpdatedAssetsCounts: () => void;
}

type CatalogBrowserHostState = {
  readonly defaultProjectId?: string;
}

class CatalogBrowserHost extends React.Component<CatalogBrowserHostProps, CatalogBrowserHostState> {

  public constructor(props: CatalogBrowserHostProps) {
    super(props);
    this.state = { defaultProjectId: undefined };
  }

  // subspaceName name here is set to a default value instead of undefined. This is in lieu of having logic in SpaceInfo to handle the case where this is undefined. database/databaseWithCrass was chosen because it is the first content type the user is shown when they enter the page. In the event that the user refreshes the External Assets page while it is showing the tables page, the subspace explorer will overwrite the subspace name set here.
  private spaceInfo: SpaceInfo = {
    spaceType: 'catalog',
    subspaceName:'databaseWithCrass',
    item: SpaceItemForNonEntitySpace
  };

  private fetchDefaultProjectId = () => {
    // IProjectInfo doesn't seem to have any property differentiating default projects from
    // all the others. This maybe makes sense, since the default project is ?just? the first
    // one created. As a proxy for this non-existent property, the default project will always
    // have the lowest ID.
    // ProjectResource doesn't seem to have any Order By field that maps to a project's ID.
    // The default project seems to always be the first one however, so we can get by just
    // grabbing that rather than having to iterate through them all.
    Projects.all()
      .withFilters([Filter.fieldEq(FilterFieldNames.topLevelProject, true)])
      .getFirst()
      .then((project) => {
        this.setState({defaultProjectId: project ? project.id : undefined});
      })
      .catch(() => {
        // defaultProjectId is already undefined.
      });
  };

  public componentDidMount() {
    this.props.fetchUpdatedAssetsCounts();
    this.fetchDefaultProjectId();
  }

  public componentDidUpdate() {
    this.props.fetchUpdatedAssetsCounts();
  }

  private renderDatabasesExplorer = (routeLocationSearch: string): JSX.Element => {
    return <CatalogBrowserSubspaceExplorer
      key="database"
      subspaceName={'databaseWithCrass'}
      routeLocationSearch={routeLocationSearch}
      contentDefinition={DatabaseDefinition}
      contentType={DatabaseContentType}
    />;
  };

  private renderTablesExplorer = (routeLocationSearch: string): JSX.Element => {
    return <CatalogBrowserSubspaceExplorer
      key="table"
      subspaceName={'tableWithCrass'}
      routeLocationSearch={routeLocationSearch}
      contentDefinition={TableDefinition}
      contentType={TableContentType}
    />;
  };

  public render(): JSX.Element {
    let result: WebClientApiContentTypeCountsResponse | undefined;

    if (this.props.assetCountResult) {
      result = {
        type: 'SUCCESS',
        databasesCount: this.props.assetCountResult.databaseWithCrass,
        tablesCount: this.props.assetCountResult.tableWithCrass,
      };
    }

    return (
      <div className="tb-fill" style={{ display: 'flex' }}>
        <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
          <EmbeddableCatalogBrowserWithoutRouter
            client={catalogApolloClient}
            siteUrls={this.props.urls}
            apiRequests={CatalogApiRequests}
            uiActions={DataCatalogUIActionsService.instance()}
            featureFlags={catalogFeatureFlagsAdapter()}
            renderDatabasesExplorer={this.renderDatabasesExplorer}
            renderTablesExplorer={this.renderTablesExplorer}
            registerErrorStore={registerErrorStore}
            unregisterErrorStore={unregisterErrorStore}
            hideLaunchEditOnMobile={hideLaunchEditOnMobile()}
            newFlowFromCatalog={FeatureFlags.flowsFromCatalogEnabled && !hideLaunchEditOnMobile()}
            projectIdForNewDocuments={this.state.defaultProjectId}
            areUsersVisible={this.props.areUsersVisible}
            webClientResult={result}
          />
        </ConnectedSpaceInfoProvider>
      </div>
    );
  }
}

export const WrappedCatalogBrowserHost = CatalogBrowserHost;
